// Typed client for /api/v1/tickets/* over the shared authenticated fetch core
// (lib/http.ts — Firebase auth + App Check + RFC-7807). Types are local
// (lib/types.ts) mirroring the Go DTOs.
import { request, uploadBinary, q } from './http';

import type {
  CityRef,
  Contact,
  DiscoverStationsResponse,
  Issue,
  IssueAck,
  OperatorRef,
  PaymentOrderResponse,
  Ticket,
  TransactionAck,
  TransitDraft,
} from './types';

export interface SearchBody {
  cityCode: string;
  fromStationCode: string;
  toStationCode: string;
  quantity?: number;
  vehicleCategory?: 'METRO' | 'BUS';
  // Route the fare search directly to the BPP resolved during station discovery.
  // Without these the search broadcasts and the resulting fare options carry no
  // bpp routing, so the follow-up select has no bpp_uri to post to.
  bppId?: string;
  bppUri?: string;
  // Reuse the city-discovery (search1) transaction so the catalog→station search
  // pair shares one transaction_id, per the ONDC TRV11 2-step search flow. Omitted
  // on a cached-catalog discovery (no search1 txn) → the backend opens a fresh txn.
  transactionId?: string;
}

// Opaque fare: the server recovers provider/fulfillment (from on_search by itemId) and the BPP (from the
// transaction), so select carries only the fare key + quantity.
export interface SelectBody {
  transactionId: string;
  itemId: string;
  quantity?: number;
}

// Identity (item/provider/fulfillment) is server-owned — derived from the stored on_select — so init
// and confirm carry only the new input (quantity + billing [+ payment proof]).
export interface InitBody {
  transactionId: string;
  quantity?: number;
  billingName: string;
  billingPhone: string;
  billingEmail?: string;
}

export interface ConfirmBody {
  transactionId: string;
  quantity?: number;
  billingName: string;
  billingPhone: string;
  billingEmail?: string;
  razorpayOrderId: string;
  razorpayPaymentId: string;
  razorpaySignature: string;
}

// SOFT (refund preview) → CONFIRM (finalize) is the rider-facing handshake. ABANDON reverts a
// SOFT preview back to ACTIVE with no BPP round-trip — a soft cancel commits nothing on the BPP, so
// backing out must never leave the order stranded in SOFT_CANCEL (the backend also auto-expires it).
export interface CancelBody {
  reason?: string;
  stage: 'SOFT' | 'CONFIRM' | 'ABANDON';
  fulfillmentId?: string;
}

// ChangeDestinationBody drives the TRV11 2.0.1 end-stop change (bus-only). 3-leg
// SOFT (re-quote) → CONFIRM (finalize) → PAYMENT (settle the fare difference); the
// webapp wires SOFT→CONFIRM. The PAYMENT stage needs a BPP paymentId surfaced via
// on_update, which the Ticket DTO does not yet expose.
export interface ChangeDestinationBody {
  fulfillmentId: string;
  stage: 'SOFT' | 'CONFIRM' | 'PAYMENT';
  endStopName?: string;
  endStopCode?: string;
  endStopGps?: string;
  paymentId?: string;
  paymentTxnId?: string;
  paymentAmount?: string;
}

// ConfirmVehicleBody drives the TRV11 2.0.1 bus confirmation update (bus-only):
// stamp the boarded vehicle registration (vehicle-based) or claim the ticket
// authorization (user-based).
export interface ConfirmVehicleBody {
  fulfillmentId: string;
  vehicleRegistration?: string;
  claimAuthorization?: boolean;
}

export interface RaiseIssueBody {
  category?: string;
  subCategory?: string;
  description: string;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  // Public, BPP-fetchable screenshot URLs (from uploadIssueAttachment). The seller
  // fetches these directly, so they must be public (not auth-gated) URLs.
  images?: string[];
  fulfillmentId?: string;
  itemId?: string;
}

export const ticketsApi = {
  cities: () => request<{ cities: CityRef[] }>('GET', '/api/v1/tickets/transit/cities'),
  // Pickable operators (BPPs) for a city — a picker is shown only when more than one is returned.
  operators: (cityCode: string) =>
    request<{ operators: OperatorRef[] }>(
      'GET',
      `/api/v1/tickets/transit/operators?city=${q(cityCode)}`
    ),
  // Catalog-driven discovery: "ready" → stations inline (cached); "discovering" →
  // poll draft(transactionId) until its `stations` arrives. A non-empty operatorBppId routes
  // discovery directly to that operator (required to disambiguate a multi-operator city).
  // refresh=true forces a fresh broadcast discovery (bypasses the cached BPP) so a degraded seller
  // can be re-picked to a healthy one after a search failure.
  stations: (
    cityCode: string,
    vehicleCategory: 'METRO' | 'BUS' = 'METRO',
    operatorBppId = '',
    refresh = false
  ) =>
    request<DiscoverStationsResponse>(
      'GET',
      `/api/v1/tickets/transit/stations?city=${q(cityCode)}&vehicleCategory=${q(vehicleCategory)}` +
        `${operatorBppId ? `&operatorBppId=${q(operatorBppId)}` : ''}${refresh ? '&refresh=true' : ''}`
    ),
  search: (body: SearchBody) =>
    request<TransactionAck>('POST', '/api/v1/tickets/transit/search', body),
  draft: (txnId: string) =>
    request<TransitDraft>('GET', `/api/v1/tickets/transit/transactions/${q(txnId)}`),
  select: (body: SelectBody) =>
    request<TransactionAck>('POST', '/api/v1/tickets/transit/select', body),
  init: (body: InitBody) => request<TransactionAck>('POST', '/api/v1/tickets/transit/init', body),
  paymentOrder: (transactionId: string) =>
    request<PaymentOrderResponse>('POST', '/api/v1/tickets/transit/payment-order', {
      transactionId,
    }),
  confirm: (body: ConfirmBody) =>
    request<TransactionAck>('POST', '/api/v1/tickets/transit/confirm', body),
  // status(ref_id) recovery: when a confirm's on_confirm was late/NACK'd, this fires the ONDC
  // status call so the BPP re-sends the order (on_status) and the ticket materialises. The server
  // keys on ref_id (= transactionId) when no orderId is supplied.
  status: (transactionId: string) =>
    request<TransactionAck>('POST', '/api/v1/tickets/transit/status', { transactionId }),

  listTickets: (limit = 20, offset = 0) =>
    request<{ tickets: Ticket[] }>('GET', `/api/v1/tickets?limit=${limit}&offset=${offset}`),
  ticket: (id: string) => request<Ticket>('GET', `/api/v1/tickets/${q(id)}`),
  cancel: (id: string, body: CancelBody) =>
    request<TransactionAck>('POST', `/api/v1/tickets/${q(id)}/cancel`, body),
  // TRV11 2.0.1 bus-only post-confirm flows.
  changeDestination: (id: string, body: ChangeDestinationBody) =>
    request<TransactionAck>('POST', `/api/v1/tickets/${q(id)}/change-destination`, body),
  confirmVehicle: (id: string, body: ConfirmVehicleBody) =>
    request<TransactionAck>('POST', `/api/v1/tickets/${q(id)}/confirm-vehicle`, body),
  support: (id: string) => request<Contact>('POST', `/api/v1/tickets/${q(id)}/support`),

  raiseIssue: (id: string, body: RaiseIssueBody) =>
    request<IssueAck>('POST', `/api/v1/tickets/${q(id)}/issues`, body),
  // Upload one issue screenshot → public, BPP-fetchable URL to pass in raiseIssue images[].
  uploadIssueAttachment: (file: File) =>
    uploadBinary<{ url: string }>('/api/v1/tickets/issues/attachments', file).then((r) => r.url),
  listAllIssues: (limit = 50, offset = 0) =>
    request<{ issues: Issue[] }>('GET', `/api/v1/tickets/issues?limit=${limit}&offset=${offset}`),
  listIssues: (id: string) =>
    request<{ issues: Issue[] }>('GET', `/api/v1/tickets/${q(id)}/issues`),
  issue: (issueId: string) => request<Issue>('GET', `/api/v1/tickets/issues/${q(issueId)}`),
  updateIssue: (issueId: string, body: { actionCode?: string; shortDesc?: string }) =>
    request<IssueAck>('POST', `/api/v1/tickets/issues/${q(issueId)}/update`, body),
  escalateIssue: (issueId: string, body: { shortDesc?: string }) =>
    request<IssueAck>('POST', `/api/v1/tickets/issues/${q(issueId)}/escalate`, body),
  closeIssue: (issueId: string, body: { rating?: string }) =>
    request<IssueAck>('POST', `/api/v1/tickets/issues/${q(issueId)}/close`, body),
};
